import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, CheckCircle2, Gift } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { useMT } from "../lib/miniI18n";
import { MINI_GAMES } from "./MiniGamesGrid";

let cache = null;
const listeners = new Set();
export const refreshMissions = () => api.get("/mini/missions").then((r) => { cache = r.data; listeners.forEach((f) => f(cache)); return cache; }).catch(() => null);

// Shared hook: today's missions (cached, refreshed on 'rl-missions' event)
export function useMissions() {
  const { user } = useAuth();
  const [m, setM] = useState(cache);
  useEffect(() => {
    if (!user) return undefined;
    listeners.add(setM);
    refreshMissions();
    const h = () => refreshMissions();
    window.addEventListener("rl-missions", h);
    return () => { listeners.delete(setM); window.removeEventListener("rl-missions", h); };
  }, [user]);
  return user ? m : null;
}

export function ProgressRing({ value, max, size = 34, stroke = 3.5, children, done }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r, pct = Math.min(1, max ? value / max : 0);
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size} className="-rotate-90"><circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(148,163,184,.35)" strokeWidth={stroke} fill="none" /><circle cx={size / 2} cy={size / 2} r={r} stroke={done ? "#10b981" : "#f59e0b"} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset .6s ease" }} /></svg>
      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black">{children}</span>
    </span>
  );
}

export function MissionsCard() {
  const m = useMissions();
  const { lang } = useI18n();
  const mt = useMT();
  const navigate = useNavigate();
  if (!m || m.enabled === false) return null;
  const doneCount = m.missions.filter((x) => x.done).length;
  return (
    <div className="rl-card p-3 mt-4" data-testid="missions-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display font-bold text-slate-900 flex items-center gap-2"><Target className="w-4 h-4 text-amber-500" />{mt("Daily Missions")} <span className="text-[10px] font-bold text-slate-400">{doneCount}/3</span></h2>
        <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1" data-testid="missions-earned"><Gift className="w-3 h-3" />₹{m.reward} {mt("each")} · +₹{m.bonus} {mt("all 3")}</span>
      </div>
      <div className="space-y-1.5">
        {m.missions.map((x) => {
          const g = x.game ? MINI_GAMES.find((y) => y.id === x.game) : null;
          const lottery = String(x.kind || "").startsWith("book_");
          return (
            <button key={x.id} onClick={() => navigate(g ? g.to : lottery ? "/" : "/play")} className={`w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left ${x.done ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50 border border-slate-200"}`} data-testid={`mission-${x.id}`}>
              <ProgressRing value={x.progress} max={x.target} done={x.done}>{x.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="text-slate-700">{x.kind === "book_amount" ? `₹${x.progress}` : `${x.progress}/${x.target}`}</span>}</ProgressRing>
              <span className="flex-1 min-w-0"><span className={`block text-xs font-bold ${x.done ? "text-emerald-700 line-through" : "text-slate-900"}`}>{lang === "ta" && x.ta ? x.ta : x.title}</span><span className="block text-[10px] text-slate-500">{x.done ? `₹${m.reward} ${mt("credited")}` : `${mt("Reward")} ₹${m.reward}`}</span></span>
              {!x.done && <span className="text-[10px] font-black text-blue-600">{mt("PLAY")} ›</span>}
            </button>
          );
        })}
      </div>
      {m.all_done && <div className="mt-2 text-center text-xs font-bold text-emerald-700 bg-emerald-100 rounded-xl py-1.5 rl-pop" data-testid="missions-all-done">🏆 {mt("All missions done! Bonus")} ₹{m.bonus} {mt("credited · new missions tomorrow")}</div>}
    </div>
  );
}
